/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import { useState, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid } from "@mui/x-data-grid";
import { Alert, Button, Paper } from "@mui/material";
import PageContainer from "../page-container/page-container";
import LoadingScreen from "../progress/loading-screen";
import { IgnoreList } from "./types";
import useIgnoreLists from "./use-ignore-list";
import getColumns from "./data-grid";
import IgnoreListDelete from "./delete-dialog";
import AddIgnoreListDialog from "./add-dialog";
import CustomToolbar from "../grid-toolbar/custom-toolbar";
import { useNotification } from "../../context/notification-provider";
import { ENTITY_TYPE_MAP } from "./constants";
import { DATA_GRID_HEIGHT, dataGridStyles } from "./styles";
import { useIgnoreListEntities, useProcessedIgnoreList } from "./utils";

const IgnoreLists = () => {
  const { showNotification } = useNotification();
  const { data: ignoreListsData, isLoading, error, mutate } = useIgnoreLists();

  const [selectedIgnoreList, setSelectedIgnoreList] =
    useState<IgnoreList | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [entityType, setEntityType] = useState<string>(ENTITY_TYPE_MAP.EMAILS);

  // Use custom hooks for data processing
  const ignoreList = useProcessedIgnoreList(ignoreListsData, entityType);
  const entities = useIgnoreListEntities(ignoreListsData);

  const selectedEntity = useMemo(
    () => entities.find((entity) => entity.type === entityType),
    [entities, entityType],
  );

  const handleOnRefreshClick = useCallback(() => {
    showNotification({
      message: "Refreshing ignore list data...",
      severity: "info",
      duration: 2000,
    });
    mutate();
  }, [showNotification, mutate]);

  const handleOnDelete = useCallback((selectedElement: IgnoreList) => {
    setSelectedIgnoreList(selectedElement);
  }, []);

  const handleOnDeleteClose = useCallback(() => {
    setSelectedIgnoreList(null);
    mutate();
  }, [mutate]);

  const handleAddClick = useCallback(() => {
    setIsAddDialogOpen(true);
  }, []);

  const handleOnAddDialogClose = useCallback(() => {
    setIsAddDialogOpen(false);
    mutate();
  }, [mutate]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <PageContainer
      title="Ignore List"
      actions={
        <Button
          variant="contained"
          onClick={handleAddClick}
          startIcon={<AddIcon />}
        >
          Add
        </Button>
      }
    >
      <Box
        sx={{
          flex: 1,
          width: "100%",
        }}
      >
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading ignore list data.
          </Alert>
        ) : (
          <Paper
            elevation={2}
            sx={{
              height: DATA_GRID_HEIGHT,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <DataGrid
              rows={ignoreList}
              columns={getColumns({ entityType, onDelete: handleOnDelete })}
              disableRowSelectionOnClick
              loading={isLoading}
              slots={{ toolbar: CustomToolbar }}
              localeText={{
                noRowsLabel:
                  entityType === "all"
                    ? "Select an entity type to view ignore list entries"
                    : "No entries found for this entity type",
              }}
              sx={dataGridStyles}
              showToolbar
              slotProps={{
                toolbar: {
                  filterType: entityType,
                  filterTypes: entities,
                  onFilterTypeChange: setEntityType,
                  onRefreshClick: handleOnRefreshClick,
                } as any,
                loadingOverlay: {
                  variant: "circular-progress",
                  noRowsVariant: "circular-progress",
                },
                baseIconButton: {
                  size: "small",
                },
              }}
            />
          </Paper>
        )}
      </Box>
      {selectedIgnoreList && (
        <IgnoreListDelete
          open={Boolean(selectedIgnoreList)}
          selectedIgnoreList={selectedIgnoreList}
          entityType={entityType}
          onClose={handleOnDeleteClose}
        />
      )}
      {isAddDialogOpen && (
        <AddIgnoreListDialog
          entities={entities}
          selectedEntityType={selectedEntity?.entityType?.toString()}
          open={isAddDialogOpen}
          onClose={handleOnAddDialogClose}
        />
      )}
    </PageContainer>
  );
};
export default IgnoreLists;
